import API from "serviceshift-ui/api-client";

// Mute specific error messages as flash
const mutedErrorMessages = [
  "Email Address Is Already Taken",
  "not_covered_error"
];

// An unhydrated frontend entity serializes its `id = -1` default (or a literal
// `undefined`) into the URL, producing backend RecordNotFound 404s that lock up
// the UI. Block those requests before they leave the browser and report them so
// the originating flow is visible in Sentry. Scoped to quote/customer/user
// resources so the scheduler's legitimate `-1` "unassigned" sentinel is untouched.
const SENTINEL_ID_PATH =
  /\/(?:quotes|customers|users)\/(?:-1|undefined)(?:[/?]|$)/;
const SENTINEL_ID_QUERY =
  /[?&](?:customer_id|user_id|quote_id)=(?:-1|undefined)(?:&|$)/;

export const hasSentinelId = (url) =>
  !!url && (SENTINEL_ID_PATH.test(url) || SENTINEL_ID_QUERY.test(url));

const reportSentinelRequest = (url, method) => {
  const label = `${(method || "get").toUpperCase()} ${url}`;
  // eslint-disable-next-line no-console
  console.error(`Blocked API request with sentinel id: ${label}`);
  window.Sentry?.withScope((scope) => {
    scope.setTag("sentinel_id_request", "true");
    scope.setExtra("request_url", url);
    scope.setExtra("request_method", method);
    window.Sentry.captureException(
      new Error(`Blocked API request with sentinel id: ${label}`)
    );
  });
};

const responseInterceptor = (error) => {
  if (error.response) {
    if (error.response.status === 401) {
      // Skip redirect for login form requests
      if (error.config.headers.skipRedirectOn401) {
        return Promise.reject(error);
      }
      // Slight delay so UI has a chance to
      // display error message before redirect
      setTimeout(() => {
        window.location.href = window.location.origin;
      }, 500);
      return Promise.reject(error);
    }
    if (
      error.response.data?.includes &&
      error.response.data?.includes("Can't verify CSRF token")
    ) {
      setTimeout(() => {
        window.location.reload();
      }, 500);
      window.notify.error("An unexpected error occurred, please try again.");
      return Promise.reject(error);
    }
    if (
      !mutedErrorMessages.includes(error.response.data.message) &&
      !error.config.headers.ignoreError
    ) {
      const errorMessage =
        error.response.data.message ||
        "An unexpected error occurred, please try again.";
      window.notify.error(errorMessage);
    }
  }
  return Promise.reject(error);
};

export const requestInterceptor = (config) => {
  if (hasSentinelId(config.url)) {
    reportSentinelRequest(config.url, config.method);
    return Promise.reject(
      new Error(`Blocked API request with sentinel id: ${config.url}`)
    );
  }

  if (config.headers["acceptHTML"]) return config;

  const isFormData = config.data instanceof FormData;

  if (!isFormData) {
    config.headers["Accept"] = "application/json";
    config.headers["Content-Type"] = "application/json";
  }

  return config;
};

API.ejectResponseInterceptor();
API.attachResponseInterceptor(responseInterceptor);
API.ejectRequestInterceptor();
API.attachRequestInterceptor(requestInterceptor);
